import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import OrderNowModal from "models/orderNowModal";
import { useSettings } from "context/SettingsContext";
import { t } from "context/translations";
import { ProductSwiperShimmer } from "components/shimmer/productSwiperShimmer";
import { assetUrl } from "utils/api";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { FaChevronRight, FaStar, FaTruck } from "react-icons/fa";
import { ChevronLeft, ChevronRight } from "lucide-react";

const ProductSwiper = ({
  data = [],
  loading = false,
  link = "",
  title = "",
  icon = null,
}) => {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [slidesPerView, setSlidesPerView] = useState(2);
  const { theme, language } = useSettings();
  const isDark = theme === "dark";

  useEffect(() => {
    const updateSlidesPerView = () => {
      const width = window.innerWidth;

      if (width >= 1280) {
        setSlidesPerView(5);
      } else if (width >= 1024) {
        setSlidesPerView(4);
      } else if (width >= 640) {
        setSlidesPerView(3);
      } else {
        setSlidesPerView(2);
      }
    };

    updateSlidesPerView();
    window.addEventListener("resize", updateSlidesPerView);

    return () => window.removeEventListener("resize", updateSlidesPerView);
  }, []);

  const showNavigation = (data?.length || 0) > slidesPerView;

  useEffect(() => {
    if (
        !swiperInstance ||
        !showNavigation ||
        !prevRef.current ||
        !nextRef.current
    ) {
      return;
    }

    swiperInstance.params.navigation.prevEl = prevRef.current;
    swiperInstance.params.navigation.nextEl = nextRef.current;
    swiperInstance.navigation.destroy();
    swiperInstance.navigation.init();
    swiperInstance.navigation.update();
  }, [swiperInstance, showNavigation]);

  if (loading) {
    return (
      <div className="relative md:max-w-7xl mx-auto px-4 sm:px-4 2xl:px-0 lg:px-8 pt-5 md:pb-4 pb-0">
        {title && (
          <div className="flex justify-between pt-5 pb-5">
            <div
              className={`h-6 w-40 rounded animate-shimmer ${
                isDark ? "bg-gray-700" : "bg-gray-300"
              }`}
            />
            <div
              className={`h-6 w-20 rounded animate-shimmer ${
                isDark ? "bg-gray-700" : "bg-gray-300"
              }`}
            />
          </div>
        )}

        <div className="relative flex overflow-x-auto gap-5 pb-5 hide-scrollbar">
          {Array.from({ length: 6 }).map((_, i) => (
            <ProductSwiperShimmer key={i} />
          ))}

          <style>{`
            .hide-scrollbar::-webkit-scrollbar { display: none; }
          `}</style>
        </div>
      </div>
    );
  }

  if (!loading && (!data || data.length === 0)) {
    return null;
  }

  return (
    <div className="relative md:max-w-7xl mx-auto sm:px-4 2xl:px-0 lg:px-8 md:pb-4 pb-0">
      <div className="rounded bg-white px-4 pb-5">
        {title && (
          <div className="flex items-center justify-between md:pt-5 pt-1 pb-5">
            <div className="flex items-center gap-2">
              <div>
                <h2 className="bg-[linear-gradient(87deg,#f0522d_11.97%,#f4801f_37.45%,#fbaf42_59.69%,#852df0_83.31%)] bg-clip-text text-xl font-semibold text-transparent md:text-2xl">
                  {title}
                </h2>
                <div className="mt-1 h-[2px] w-12 rounded-full bg-gradient-to-r from-indigo-600 to-pink-500" />
              </div>
            </div>
            {link && (
              <Link
                to={link}
                className={`flex items-center gap-1 rounded p-1 font-medium text-sm text-[#F26E21] md:text-base `}
              >
                {t("viewAll", language)}
                <FaChevronRight className="text-sm" />
              </Link>
            )}
          </div>
        )}

        <div className="relative">
          <Swiper
            modules={[Navigation]}
            onSwiper={setSwiperInstance}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            speed={700}
            rewind
            watchOverflow
            spaceBetween={12}
            slidesPerView={2}
            breakpoints={{
              640: { slidesPerView: 3, spaceBetween: 14 },
              1024: { slidesPerView: 4, spaceBetween: 16 },
              1280: { slidesPerView: 5, spaceBetween: 18 },
            }}
            className="gadget-swiper pb-2"
          >
            {data.map((item, index) => {
              const isOutOfStock = item.quantity === 0;
              const firstVariant = item.variants?.[0] || {};
              const firstImage = firstVariant.images?.[0]?.thumb || "";
              const selectedTitle = firstVariant.title || "-";
              const selectedPrice = firstVariant.flashPrice || 0;
              const selectedDiscount = firstVariant.discount || 0;
              const originalPrice = selectedDiscount || 0;
              const flashPrice = selectedPrice || 0;
              const freeVariantDelivery = firstVariant?.freeVariantDelivery;
              const discountPercent =
                originalPrice > 0
                  ? Math.ceil(
                      ((originalPrice - flashPrice) / originalPrice) * 100,
                    )
                  : 0;

              return (
                <SwiperSlide
                  key={`${item.item_id}-${index}`}
                  className="h-auto"
                >
                  <div
                    className={`group relative mb-3 flex h-full flex-col overflow-hidden rounded-[18px] border border-slate-200 bg-white transition-all shadow-md duration-300  hover:shadow-[0_12px_26px_rgba(15,23,42,0.14)] ${
                      isOutOfStock ? "cursor-not-allowed opacity-45" : ""
                    }`}
                  >
                    {isOutOfStock && (
                      <div className="absolute inset-0 flex items-center justify-center z-20">
                        <div className="bg-red-600 text-white text-2xl font-bold px-3 py-2 rounded-lg shadow-lg">
                          {t("stockOut", language)}
                        </div>
                      </div>
                    )}

                    <Link
                      to={`/details/${item.catalogue}/${item.item_id}`}
                      className="block"
                    >
                      <div className="relative flex aspect-[1/1.08] items-center justify-center overflow-hidden bg-white px-2 pt-6">
                        <img
                          src={assetUrl(firstImage)}
                          alt={selectedTitle}
                          className="max-h-full w-full object-contain object-center transition-transform duration-500 group-hover:scale-105"
                          draggable={false}
                        />
                        <div className="absolute top-2 left-2">
                          {discountPercent > 0 && (
                            <div className="px-2 md:h-5 h-6 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold text-xs">
                              {discountPercent}% OFF
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>

                    <div className="flex flex-1 flex-col px-4 pb-3 pt-2">
                      <div className={`${isDark?"text-white":"text-gray-800"} text-center font-medium tracking-wide mb-1 text-sm leading-5 min-h-10 line-clamp-2`}>
                        {selectedTitle}
                      </div>

                      <div className="my-1 flex items-center justify-center gap-2">
                        <div className=" font-bold text-[#ff6a00] md:text-[18px] ">
                          Tk. {selectedPrice}
                        </div>
                        {selectedDiscount > selectedPrice && (
                          <div className="relative text-[12px] text-slate-400">
                            <span>Tk. {selectedDiscount}</span>
                            <div className="absolute left-0 right-0 top-1/2 h-px bg-slate-400" />
                          </div>
                        )}
                      </div>
                      {freeVariantDelivery === 1 && (
                        <div
                          className="flex items-center justify-center gap-1 px-3 py-1  rounded-full
         text-emerald-700 text-sm font-medium"
                        >
                          <FaTruck className="text-sm" />
                          {t("freeDeliveryBadge", language)}
                        </div>
                      )}
                      <div className="my-1 flex items-center justify-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <FaStar
                            key={star}
                            className="text-yellow-400 text-sm"
                          />
                        ))}
                        <span className="ml-1 text-sm font-medium text-gray-600">
                          (5.0)
                        </span>
                      </div>

                      {/* Add to Cart Button */}
                      {!isOutOfStock ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItem(item);
                            setOrderModalOpen(true);
                          }}
                          className="mt-2 inline-flex w-full items-center justify-center rounded-full border border-[#ff6a00] bg-[#fff4ec] px-4 py-1 text-sm font-semibold text-[#ff6a00] transition-colors hover:bg-[#ff6a00] hover:text-white"
                        >
                          {t("order", language)}
                        </button>
                      ) : (
                        <div className="mt-auto h-10" />
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>

          {showNavigation && (
            <button
              ref={prevRef}
              type="button"
              aria-label="Previous slide"
              className={`absolute left-0 top-1/2 z-10 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full border shadow ${
                isDark ? "bg-gray-100" : "bg-white"
              } text-darkBgColor hover:bg-white/80`}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {showNavigation && (
            <button
              ref={nextRef}
              type="button"
              aria-label="Next slide"
              className={`absolute right-0 top-1/2 z-10 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full border shadow ${
                isDark ? "bg-gray-100" : "bg-white"
              } text-darkBgColor hover:bg-white/80`}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
      <OrderNowModal
        isOpen={orderModalOpen}
        item={selectedItem}
        onClose={() => setOrderModalOpen(false)}
      />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default ProductSwiper;
